import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  FiEdit2,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiTool,
  FiFilter,
  FiHash,
  FiFolder,
  FiLock,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import Api from '../../Api';
import { usePermission } from '../../hooks/usePermission';
import {
  Button,
  Card,
  Input,
  Modal,
  PageComponent,
  Select,
  MultiSelect,
  Skeletons,
  Table,
} from '../../components';
import TableActions from '../../components/Table/TableActions';
import EntityFilesModal from '../../components/EntityFilesModal';
import MachineryForm from '../../components/MachineryForm';
import '../CompanyManager/style.css';

export default function MachineryManager() {
  const navigate = useNavigate();
  const permissions = usePermission('management');
  const [loading, setLoading] = useState(true);
  const [machineries, setMachineries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Company management
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('all');
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedMachinery, setSelectedMachinery] = useState(null);
  const [isFilesModalOpen, setIsFilesModalOpen] = useState(false);
  const [selectedMachineryForFiles, setSelectedMachineryForFiles] = useState(null);

  useEffect(() => {
    loadCompanies();
  }, []);

  async function loadCompanies() {
     try {
       const { data } = await Api.get('/companies');
       setCompanies(data || []);
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  }

  const loadMachineries = useCallback(async () => {
    try {
      setLoading(true);
      let data = [];

      if (companyFilter === 'all') {
        const promises = companies.map(c => 
          Api.get(`/companies/${c.id}/machineries`)
            .then(response => {
                const items = response.data || [];
                return items.map(v => ({ ...v, company: c }));
            })
            .catch((err) => {
              console.error(`Error loading machineries for company ${c.id}:`, err);
              return [];
            })
        );
        const results = await Promise.all(promises);
        data = results.flat();
      } else {
        const selectedComp = companies.find(c => String(c.id) === String(companyFilter));
        if (selectedComp) {
            const response = await Api.get(`/companies/${selectedComp.id}/machineries`);
            data = (response.data || []).map(v => ({ ...v, company: selectedComp }));
        }
      }
      setMachineries(data);
      setHasLoadedOnce(true);
    } catch (error) {
      console.error('Error loading machineries:', error);
    } finally {
      setLoading(false);
    }
  }, [companyFilter, companies]);

  useEffect(() => {
    if (permissions.canView && companies.length > 0) {
      if (companyFilter !== 'all' && !companies.find(c => String(c.id) === String(companyFilter))) {
        setCompanyFilter('all');
      } else {
        loadMachineries();
      }
    }
  }, [companies, companyFilter, loadMachineries, permissions.canView]);

  const companyOptions = useMemo(() => [
    { label: 'TODAS', value: 'all' },
    ...companies.map(c => ({ 
      label: c.corporate_name || c.trade_name || `Empresa ${c.id}`, 
      value: String(c.id) 
    }))
  ], [companies]);

  const filteredMachineries = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const allValue = 'all';
    const specificIds = companyFilterValues.filter(v => v !== allValue);
    const hasAllSelected = companyFilterValues.includes(allValue);

    return machineries.filter(machinery => {
      const matchesSearch =
        !term ||
        machinery.tag?.toLowerCase().includes(term) ||
        machinery.model?.toLowerCase().includes(term) ||
        machinery.brand?.toLowerCase().includes(term);

      const companyId = machinery.company_id || machinery.company?.id;
      const matchesCompany =
        hasAllSelected ||
        specificIds.length === 0 ||
        specificIds.includes(String(companyId));

      return matchesSearch && matchesCompany;
    });
  }, [machineries, searchTerm, companyFilterValues]);

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      const allIds = companies.map(c => String(c.id));
      if (specificIds.length === allIds.length && allIds.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterValues(nextValues);

    const idsWithoutAll = nextValues.filter(v => v !== allValue);
    if (idsWithoutAll.length === 1) {
      setCompanyFilter(idsWithoutAll[0]);
    } else {
      setCompanyFilter('all');
    }
  }

  function handleCreate() {
    setSelectedMachinery(null);
    setIsModalOpen(true);
  }

  function handleEdit(machinery) {
    setSelectedMachinery(machinery);
    setIsModalOpen(true);
  }

  function handleFiles(machinery) {
    setSelectedMachineryForFiles(machinery);
    setIsFilesModalOpen(true);
  }

  function handleDeleteClick(machinery) {
    setSelectedMachinery(machinery);
    setIsDeleteModalOpen(true);
  }

  async function handleConfirmDelete() {
    if (!selectedMachinery) return;
    try {
      const companyId = selectedMachinery.company_id || selectedMachinery.company?.id;
      if (companyId) {
          await Api.delete(`/companies/${companyId}/machineries/${selectedMachinery.id}`);
          await loadMachineries();
          setIsDeleteModalOpen(false);
          setSelectedMachinery(null);
      }
    } catch (error) {
      console.error('Error deleting machinery:', error);
    }
  }

  const columns = [
    {
      id: 'tag',
      header: 'Maquinário',
      width: '2.5fr',
      cellClassName: 'manager-table-cell-primary',
      render: machinery => (
        <>
          <div className="manager-table-avatar">
            <FiTool size={18} />
          </div>
          <div className="manager-table-cell-text">
            <span className="manager-table-title">{machinery.tag}</span>
            <span className="manager-table-subtitle">{machinery.model}</span>
          </div>
        </>
      ),
    },
    {
      id: 'brand',
      header: 'Marca',
      width: '1.5fr',
      cellClassName: 'manager-table-cell',
      render: machinery => (
        <span className="manager-table-text">{machinery.brand || '-'}</span>
      ),
    },
    {
      id: 'year',
      header: 'Ano',
      width: '1.5fr',
      cellClassName: 'manager-table-cell',
      render: machinery => (
        <span className="manager-table-text">{machinery.year || '-'}</span>
      ),
    },
    {
      id: 'company',
      header: 'Empresa',
      width: '1.5fr',
      render: machinery => {
        const companyName = machinery.company 
          ? (machinery.company.corporate_name || machinery.company.trade_name)
          : 'N/A';
        return <span className="manager-table-text">{companyName}</span>;
      },
    },
    {
      id: 'service_station',
      header: 'Posto',
      width: '1.5fr',
      render: machinery => (
        <span className="manager-table-text">
          {machinery.serviceStation?.name || '-'}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      width: '1fr',
      cellClassName: 'manager-table-cell',
      render: machinery => {
        const isActive = machinery.active;
        return (
          <div className="manager-tags">
            <span
              className={`manager-tag ${
                isActive ? 'manager-tag-success' : 'manager-tag-danger'
              }`}
            >
              {isActive ? 'Ativo' : 'Inativo'}
            </span>
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Ações',
      width: '0.6fr',
      cellClassName: 'manager-table-cell manager-table-col-actions',
      render: machinery => (
        <TableActions>
          {permissions.canEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(machinery);
              }}
            >
              <FiEdit2 size={16} />
              Editar
            </button>
          )}
          {permissions.canEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleFiles(machinery);
              }}
            >
              <FiFolder size={16} />
              Arquivos
            </button>
          )}
          {permissions.canDelete && (
            <button
              type="button"
              className="action-danger"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(machinery);
              }}
            >
              <FiTrash2 size={16} />
              Excluir
            </button>
          )}
        </TableActions>
      ),
    },
  ];

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiLock size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar esta página.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  function MachineryManagerSkeletonWrapper() {
    const { CompanyManagerSkeleton } = Skeletons;
    return <CompanyManagerSkeleton />;
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        {loading && !hasLoadedOnce && (
          <MachineryManagerSkeletonWrapper />
        )}

        {(hasLoadedOnce || !loading) && (
          <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiTool size={22} />
                </div>
                <div>
                  <h1>Maquinários</h1>
                  <p>
                    Visualize, filtre e gerencie todos os maquinários da empresa selecionada.
                  </p>
                </div>
              </div>
              {permissions.canCreate && (
                <Button onClick={handleCreate} className="manager-header-button">
                  <FiPlus size={18} />
                  Novo Maquinário
                </Button>
              )}
            </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
                {machineries.length > 0 && (
                  <span className="manager-filters-count">
                    {filteredMachineries.length} de {machineries.length} maquinários
                  </span>
                )}
              </div>

              <div className="manager-filters-grid-custom">
                <MultiSelect
                  id="company-filter"
                  label="Empresa"
                  options={[
                    { value: 'all', label: 'TODAS' },
                    ...companies.map(c => ({
                      value: String(c.id),
                      label: c.corporate_name || c.trade_name || `Empresa ${c.id}`,
                    })),
                  ]}
                  value={companyFilterValues}
                  onChange={handleCompanyFilterChange}
                  searchable
                />
                <Input
                  id="search"
                  label="Buscar"
                  placeholder="Buscar por etiqueta, modelo ou marca..."
                  icon={FiSearch}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
            </Card>

            <div className="manager-content">
              <Table
                items={filteredMachineries}
                columns={columns}
                totalItems={filteredMachineries.length}
                emptyMessage="Nenhum maquinário encontrado."
                onRowDoubleClick={permissions.canEdit ? handleEdit : undefined}
              />
            </div>
          </>
        )}
      </div>

      <EntityFilesModal
        isOpen={isFilesModalOpen}
        onClose={() => setIsFilesModalOpen(false)}
        entity={selectedMachineryForFiles}
        entityType="machinery"
        company={selectedMachineryForFiles?.company || companies.find(c => String(c.id) === String(companyFilter))}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedMachinery ? 'Editar Maquinário' : 'Novo Maquinário'}
      >
        <MachineryForm
          companyId={companyFilter !== 'all' ? Number(companyFilter) : null}
          companies={companies}
          initialData={selectedMachinery}
          onSuccess={() => {
            setIsModalOpen(false);
            loadMachineries();
          }}
          onCancel={() => setIsModalOpen(false)}
        />
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirmar Exclusão"
      >
        <div className="delete-modal-content">
          <p>
            Tem certeza que deseja excluir o maquinário{' '}
            <span className="delete-modal-company-name">
              {selectedMachinery?.tag}
            </span>
            ?
          </p>
          <p>Essa ação não poderá ser desfeita.</p>
        </div>
        <div className="delete-modal-actions">
          <Button
            type="button"
            className="cancel-button"
            onClick={() => setIsDeleteModalOpen(false)}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleConfirmDelete}
          >
            Excluir
          </Button>
        </div>
      </Modal>
    </PageComponent>
  );
}
